import React, { Fragment, useCallback } from 'react';
import styled from '@emotion/styled';
import AddCircle from '@material-ui/icons/AddCircle';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import { Row, Col } from 'antd';
import produce from 'immer';
import { isNil } from 'lodash';

import { PanelContainer, PanelProps } from '.';

import ElementList from '../form-render/element-list';
import { FormElementChangeEvent } from '../index';
import theme from '../../../theme';
import { FormComponentType } from '../../../types/form';
import { fieldComponentRenderer } from '../form-render/default-field-renderer';

type RepeatablePanelProps = Omit<PanelProps, 'value' | 'repeatable'> & {
  value?: Record<string, any>[];
};

const AddIcon = styled(AddCircle)`
  font-size: 28px !important;
  margin: 0 10px 2px 0;
`;

const RemoveIcon = styled(RemoveCircle)`
  font-size: 28px !important;
  margin: 0 10px 2px 0;
`;

const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${theme.colorsPrimaryBlue};
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
`;

const AddButton = styled(StyledButton)`
  margin: 10px 0;
`;

const RepeatableItem = styled(Row)`
  flex-direction: column;
  padding: 10px 0;
  border-bottom: 1px solid #e8e8e8;
`;

const OrderedNumberCol = styled(Col)`
  padding: 10px 0;
  min-width: 40px; // to support at least two digits i.e. 10.

  & > p {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.85);
    text-align: right;
  }
`;

const OrderedContentCol = styled(Col)`
  flex: 1;
`;

const DEFAULT_VALUE = [{}];

const PanelRepeatable = React.memo(function PanelRepeatable({
  name: panelName,
  path: panelPath,
  elements,
  getFormData,
  fieldRenderer,
  value: panelValues = DEFAULT_VALUE,
  errors,
  disabled,
  isOrderedList,
  hiddenFields,
  onChange,
  onBlur,
  onVisibilityChange,
  onAddItem,
  onDeleteItem,
  maxRepeatable,
}: RepeatablePanelProps) {
  const defaultFieldRenderer = useCallback(
    (type: FormComponentType, attributes: any, error: any, Comp: React.ComponentType<any>) => {
      attributes.disabled = disabled;
      return fieldComponentRenderer(type, attributes, error, Comp);
    },
    [disabled]
  );

  const onAddItemHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const newPanelValues = produce(panelValues, draft => {
        draft.push({});
      });
      onChange && onChange({ name: panelName, value: newPanelValues });
      onAddItem(panelName, panelPath, newPanelValues.length - 1);
    },
    [onAddItem, onChange, panelName, panelPath, panelValues]
  );

  const onDeleteItemHandler = useCallback(
    (index: number, event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const newPanelValues = produce(panelValues, draft => {
        draft.splice(index, 1);
      });
      onChange && onChange({ name: panelName, value: newPanelValues });
      onDeleteItem(panelName, panelPath, index);
    },
    [onChange, onDeleteItem, panelName, panelPath, panelValues]
  );

  const handleOnChange = useCallback(
    (itemIndex: number, event: FormElementChangeEvent) => {
      if (onChange) {
        const panelItemPath = `${panelPath}[${itemIndex}]`;
        const { name, value, path } = event;
        if (!name) {
          return;
        }

        onChange({
          name,
          value,
          path: path || `${panelItemPath}.${name}`,
        });
      }
    },
    [onChange, panelPath]
  );

  const handleOnBlur = useCallback(
    (itemIndex: number, event: FormElementChangeEvent) => {
      if (onBlur) {
        const panelItemPath = `${panelPath}[${itemIndex}]`;
        const { name, path } = event;
        if (!name) {
          return;
        }
        if (onBlur) {
          onBlur({ name, value: undefined, path: path || `${panelItemPath}.${name}` });
        }
      }
    },
    [onBlur, panelPath]
  );

  const renderRepeatableItem = (itemIndex: number) => {
    const panelItemPath = `${panelPath}[${itemIndex}]`;

    return (
      <RepeatableItem>
        <Col>
          <Row gutter={[16, 0]}>
            <ElementList
              path={panelItemPath}
              elements={elements}
              getFormData={getFormData}
              fieldRenderer={fieldRenderer || defaultFieldRenderer}
              errors={errors}
              hiddenFields={hiddenFields}
              itemIndex={itemIndex}
              onChangeElement={onChange && handleOnChange.bind(null, itemIndex)}
              onBlurElement={onBlur && handleOnBlur.bind(null, itemIndex)}
              onVisibilityChange={onVisibilityChange}
              onAddItem={onAddItem}
              onDeleteItem={onDeleteItem}
            />
          </Row>
        </Col>
        {panelValues.length > 1 && !disabled && (
          <Col>
            <StyledButton onClick={onDeleteItemHandler.bind(null, itemIndex)} disabled={disabled}>
              <RemoveIcon />
              <span>Delete additional answer</span>
            </StyledButton>
          </Col>
        )}
      </RepeatableItem>
    );
  };

  if (panelValues && !Array.isArray(panelValues)) {
    console.error('The type of the value data must be an Array type for repeatable panels', panelValues);
    return <div>Invalid data type received for the field name &quot;{panelName}&quot;</div>;
  }

  return (
    <PanelContainer>
      {panelValues.map((_: any, itemIndex: number) => {
        return (
          <Fragment key={itemIndex}>
            {isOrderedList && (
              <Row gutter={16}>
                <OrderedNumberCol>
                  <p>
                    <strong>{itemIndex + 1}.</strong>
                  </p>
                </OrderedNumberCol>
                <OrderedContentCol>{renderRepeatableItem(itemIndex)}</OrderedContentCol>
              </Row>
            )}
            {!isOrderedList && renderRepeatableItem(itemIndex)}
          </Fragment>
        );
      })}
      {!disabled && (isNil(maxRepeatable) || panelValues.length <= maxRepeatable) && (
        <AddButton onClick={onAddItemHandler} disabled={disabled}>
          <AddIcon />
          <span>Add additional answer</span>
        </AddButton>
      )}
    </PanelContainer>
  );
});

export default PanelRepeatable;
